import { Autocomplete, Button, Card, Fade, Grid, Modal, Typography } from "@mui/material";
import { makeStyles, styled } from "@mui/styles";
import CustomTextField from "components/Form/CustomTextField";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import useDropdowns from "hooks/useDropdowns";
import useYupValidationResolver from "hooks/useYupValidationResolver";
import FormField from "layouts/applications/wizard/components/FormField";
import { Controller, useForm } from "react-hook-form";
import { APPLICANT_EMPLOYEE_ENTITIES } from "utils/constants/entityOptions";
import editEmployeeDataSchema from "./editEmployeeDataSchema";

const useStyle = makeStyles({
  container: {
    position: "absolute",
    width: "25%",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
});

const { EMPLOYMENT_STATUS_ENTITY } = APPLICANT_EMPLOYEE_ENTITIES;

const StyledTypographyError = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: "0.75rem",
}));

const EditEmployeeModal = ({ open, setOpen, onSubmit, employee }) => {
  const classes = useStyle();

  const { dropdowns: employmentStatusesField } = useDropdowns({ entity: EMPLOYMENT_STATUS_ENTITY });

  const resolver = useYupValidationResolver(
    editEmployeeDataSchema({
      employmentStatusesField,
    })
  );

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    trigger,
    getValues,
    formState: { dirtyFields, errors },
  } = useForm({
    resolver,
    defaultValues: {
      firstName: employee?.firstName ?? "",
      lastName: employee?.lastName ?? "",
      email: employee?.email ?? "",
      phone: employee?.phone ?? "",
      employmentStatus: employee?.employmentStatus ?? "",
    },
  });

  const handleSubmitFunc = (values) => {
    onSubmit(values);
    setOpen(false);
  };

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        setOpen(false);
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Fade in={open}>
        <Card className={[classes.container].join(" ")}>
          <MDTypography variant="h5" color="dark" sx={{ mt: 2, mb: 1, ml: 2 }} key="text_addNote">
            Edit Employee
          </MDTypography>
          <MDBox p={3}>
            <form onSubmit={handleSubmit(handleSubmitFunc)}>
              <Grid container xs={12} rowSpacing={3} columnSpacing={2}>
                <Grid item xs={6}>
                  <Controller
                    name="firstName"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <CustomTextField
                        {...field}
                        onChange={(e) => {}}
                        disabled
                        label="First Name"
                        InputLabelProps={{ shrink: true }}
                        showError
                        errorMessage={error?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name="lastName"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <CustomTextField
                        {...field}
                        onChange={(e) => {}}
                        disabled
                        label="Last Name"
                        InputLabelProps={{ shrink: true }}
                        showError
                        errorMessage={error?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <CustomTextField
                        {...field}
                        onChange={(e) => {}}
                        disabled
                        label="Email"
                        InputLabelProps={{ shrink: true }}
                        showError
                        errorMessage={error?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <CustomTextField
                        {...field}
                        onChange={(e) => {}}
                        disabled
                        label="Phone"
                        InputLabelProps={{ shrink: true }}
                        showError
                        errorMessage={error?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="employmentStatus"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        options={employmentStatusesField || []}
                        autoSelect
                        freeSolo
                        name="employmentStatus"
                        value={field.value}
                        onChange={(e, v) => field.onChange(v)}
                        onBlur={() => trigger("employmentStatus")}
                        renderInput={(params) => (
                          <FormField
                            {...params}
                            type="text"
                            label="Employment Status"
                            InputLabelProps={{ shrink: getValues("employmentStatus") }}
                          />
                        )}
                      />
                    )}
                  />
                  {errors?.employmentStatus && (
                    <StyledTypographyError>
                      {errors?.employmentStatus.message}
                    </StyledTypographyError>
                  )}
                </Grid>
              </Grid>
              <Grid container mt={2} justifyContent="flex-end">
                <Grid container item width="auto" spacing={3} justifyContent="flex-end">
                  <Grid item xs={6}>
                    <Button
                      color="error"
                      variant="contained"
                      fullWidth
                      onClick={async (e) => {
                        e?.stopPropagation();
                        setOpen(false);
                      }}
                    >
                      <MDTypography variant="body2" color="white">
                        Cancel
                      </MDTypography>
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button type="submit" variant="contained" fullWidth>
                      <MDTypography variant="body2" color="white">
                        Save
                      </MDTypography>
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </MDBox>
        </Card>
      </Fade>
    </Modal>
  );
};

export default EditEmployeeModal;
